import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    Grid,
    InputAdornment,
    Pagination,
    Skeleton,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { CompanyDTO } from '@root/api';
import HfDialog from '@root/app/shared/new-components/hf-dialog/HfDialog';
import HfEmptyState from '@root/app/shared/new-components/hf-empty-state/hfEmptyState';
import { companyService } from '@root/app/shared/new-services/company.service';
import { errorService } from '@root/app/shared/new-services/newError.service';
import { useTranslation } from '@root/app/shared/hooks/useTranslation';
import {
    HF_BORDERS,
    HF_BORDER_WIDTHS,
    HF_BRAND_COLORS,
    HF_CTA_COLORS,
    HF_NEUTRAL_COLORS,
    HF_RADIUS,
    HF_SPACING
} from '@root/app/shared/theme/designTokens';

const COMPANIES_PER_PAGE = 9;
const FETCH_BATCH_SIZE = 50;
const PLACEHOLDER_IMAGE = 'images/placeholder_enterprise.png';
const SEARCH_DEBOUNCE_MS = 300;

type CompanyStatusFilter = 'all' | 'active' | 'inactive';

const isAbortError = (error: unknown): boolean => {
    if (error instanceof DOMException && error.name === 'AbortError') {
        return true;
    }
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    const maybeError = error as { name?: string; code?: string };
    return maybeError.name === 'AbortError'
        || maybeError.name === 'CanceledError'
        || maybeError.code === 'ERR_CANCELED';
};

const createAbortError = (): DOMException => new DOMException('Aborted', 'AbortError');

const getAbortReason = (signal?: AbortSignal): Error =>
    signal?.reason instanceof Error ? signal.reason : createAbortError();

const loadAllMatchingCompanies = (name?: string, signal?: AbortSignal): Promise<CompanyDTO[]> =>
    new Promise((resolve, reject) => {
        const collected: CompanyDTO[] = [];
        const ids = new Set<string>();
        let settled = false;

        const settle = (apply: () => void) => {
            if (settled) {
                return;
            }
            settled = true;
            if (signal) {
                signal.removeEventListener('abort', onAbort);
            }
            apply();
        };

        const onAbort = () => {
            settle(() => reject(getAbortReason(signal)));
        };

        const rejectIfAborted = (): boolean => {
            if (!signal?.aborted) {
                return false;
            }
            onAbort();
            return true;
        };

        const loadBatch = (from: number) => {
            if (rejectIfAborted()) {
                return;
            }

            companyService.getAllCompanies((err, data) => {
                if (rejectIfAborted()) {
                    return;
                }
                if (err || !data) {
                    settle(() => reject(err));
                    return;
                }

                const page = data as { result?: CompanyDTO[]; total?: number; to?: number };
                (page.result || []).forEach((company) => {
                    if (company.id && !ids.has(company.id)) {
                        ids.add(company.id);
                        collected.push(company);
                    }
                });

                const total = page.total ?? collected.length;
                const pageEnd = page.to ?? collected.length;
                if (total > pageEnd) {
                    loadBatch(pageEnd);
                } else {
                    settle(() => resolve([...collected]));
                }
            }, name, from, from + FETCH_BATCH_SIZE, signal);
        };

        if (signal) {
            if (signal.aborted) {
                onAbort();
                return;
            }
            signal.addEventListener('abort', onAbort, { once: true });
        }

        loadBatch(0);
    });

interface HfCompanySelectorModalProps {
    open: boolean;
    currentCompanyId?: string;
    changingCompany: boolean;
    onClose: () => void;
    onSelectCompany: (company: CompanyDTO) => void;
}

const HfCompanySelectorModal: React.FC<HfCompanySelectorModalProps> = ({
    open,
    currentCompanyId,
    changingCompany,
    onClose,
    onSelectCompany
}) => {
    const { t } = useTranslation();
    const [allCompanies, setAllCompanies] = useState<CompanyDTO[]>([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<CompanyStatusFilter>('all');
    const inFlightAbortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setDebouncedSearch(searchInput.trim());
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
            inFlightAbortRef.current?.abort();
        };
    }, [open, searchInput]);

    useEffect(() => {
        if (!open) {
            return;
        }
        setPage(1);
    }, [open, debouncedSearch, statusFilter]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const abortController = new AbortController();
        inFlightAbortRef.current = abortController;
        let cancelled = false;

        const loadCompanies = async () => {
            setLoading(true);

            try {
                const data = await loadAllMatchingCompanies(
                    debouncedSearch || undefined,
                    abortController.signal
                );
                if (cancelled || abortController.signal.aborted) {
                    return;
                }
                setAllCompanies(data);
            } catch (err) {
                if (cancelled || isAbortError(err) || abortController.signal.aborted) {
                    return;
                }
                errorService.reportError(err);
                setAllCompanies([]);
            } finally {
                if (!cancelled && !abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadCompanies();

        return () => {
            cancelled = true;
            abortController.abort();
            if (inFlightAbortRef.current === abortController) {
                inFlightAbortRef.current = null;
            }
        };
    }, [open, debouncedSearch]);

    useEffect(() => {
        if (!open) {
            setSearchInput('');
            setDebouncedSearch('');
            setStatusFilter('all');
            setPage(1);
            setAllCompanies([]);
            setIsNavigating(false);
        }
    }, [open]);

    const isSearchPending = open && searchInput.trim() !== debouncedSearch;
    const showGridSkeleton = loading || isSearchPending || isNavigating;

    const filteredCompanies = useMemo(() => {
        if (statusFilter === 'active') {
            return allCompanies.filter((company) => company.status === 'ACTIVE');
        }
        if (statusFilter === 'inactive') {
            return allCompanies.filter((company) => company.status !== 'ACTIVE');
        }
        return allCompanies;
    }, [allCompanies, statusFilter]);

    const paginatedCompanies = useMemo(() => {
        const start = (page - 1) * COMPANIES_PER_PAGE;
        return filteredCompanies.slice(start, start + COMPANIES_PER_PAGE);
    }, [filteredCompanies, page]);

    const total = filteredCompanies.length;
    const pageCount = Math.max(1, Math.ceil(total / COMPANIES_PER_PAGE));
    const showEmptyState = !showGridSkeleton && paginatedCompanies.length === 0;

    useEffect(() => {
        if (!isNavigating) {
            return undefined;
        }

        const timer = window.setTimeout(() => setIsNavigating(false), 200);
        return () => window.clearTimeout(timer);
    }, [page, statusFilter, isNavigating]);

    const handlePageChange = (_: React.ChangeEvent<unknown>, nextPage: number) => {
        if (nextPage === page) {
            return;
        }
        setIsNavigating(true);
        setPage(nextPage);
    };

    const handleStatusFilterChange = (filter: CompanyStatusFilter) => {
        if (filter === statusFilter) {
            return;
        }
        setIsNavigating(true);
        setStatusFilter(filter);
    };

    const statusFilterCounts = useMemo(() => ({
        all: allCompanies.length,
        active: allCompanies.filter((company) => company.status === 'ACTIVE').length,
        inactive: allCompanies.filter((company) => company.status !== 'ACTIVE').length
    }), [allCompanies]);

    const statusFilterLabels: Record<CompanyStatusFilter, string> = {
        all: t('ALL'),
        active: t('ACTIVE'),
        inactive: t('INACTIVE')
    };

    const getStatusFilterLabel = (filter: CompanyStatusFilter) =>
        `${statusFilterLabels[filter]} (${statusFilterCounts[filter]})`;

    const renderCompanyTile = (company: CompanyDTO) => {
        const isSelected = company.id === currentCompanyId;
        const isInactive = company.status !== 'ACTIVE';

        return (
            <Box
                key={company.id}
                component='button'
                type='button'
                disabled={changingCompany}
                onClick={() => onSelectCompany(company)}
                sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: 120,
                    p: `${HF_SPACING.md}px`,
                    border: isSelected
                        ? `${HF_BORDER_WIDTHS.medium}px solid ${HF_CTA_COLORS[500]}`
                        : HF_BORDERS.divider,
                    borderRadius: `${HF_RADIUS.md}px`,
                    bgcolor: isSelected ? HF_CTA_COLORS[100] : HF_BRAND_COLORS.white,
                    cursor: changingCompany ? 'wait' : 'pointer',
                    opacity: isInactive ? 0.6 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: `${HF_SPACING.sm}px`,
                    transition: 'border-color 0.15s ease, background-color 0.15s ease',
                    '&:hover:not(:disabled)': {
                        borderColor: HF_CTA_COLORS[500],
                        bgcolor: isSelected ? HF_CTA_COLORS[100] : HF_NEUTRAL_COLORS[100]
                    }
                }}
            >
                <Box
                    component='img'
                    alt={company.name || ''}
                    src={company.imageUrl || PLACEHOLDER_IMAGE}
                    sx={{
                        width: 48,
                        height: 48,
                        objectFit: 'contain',
                        borderRadius: `${HF_RADIUS.md}px`,
                        bgcolor: HF_NEUTRAL_COLORS[100]
                    }}
                />
                <Typography
                    variant='body3'
                    fontWeight={isSelected ? 'bold' : 'regular'}
                    sx={{
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.3
                    }}
                >
                    {company.name}
                </Typography>
            </Box>
        );
    };

    const renderGridSkeleton = () => (
        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
            {Array.from({ length: COMPANIES_PER_PAGE }).map((_, index) => (
                <Grid item xs={4} sx={{ display: 'flex' }} key={index}>
                    <Stack
                        spacing={1}
                        alignItems='center'
                        justifyContent='center'
                        sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: 120,
                            p: `${HF_SPACING.md}px`,
                            border: HF_BORDERS.divider,
                            borderRadius: `${HF_RADIUS.md}px`
                        }}
                    >
                        <Skeleton variant='rounded' width={48} height={48} />
                        <Skeleton variant='rounded' width='80%' height={16} />
                        <Skeleton variant='rounded' width='55%' height={16} />
                    </Stack>
                </Grid>
            ))}
        </Grid>
    );

    const renderGridContent = () => {
        if (showGridSkeleton) {
            return renderGridSkeleton();
        }

        if (showEmptyState) {
            return (
                <Box py={4}>
                    <HfEmptyState
                        small
                        message={t('NO_RESULTS_FOUND')}
                        image='images/not_results.svg'
                    />
                </Box>
            );
        }

        return (
            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                {paginatedCompanies.map((company) => (
                    <Grid item xs={4} sx={{ display: 'flex' }} key={company.id}>
                        {renderCompanyTile(company)}
                    </Grid>
                ))}
            </Grid>
        );
    };

    return (
        <HfDialog
            open={open}
            onClose={onClose}
            title={t('CHANGE_COMPANY')}
            size='md'
        >
            <Stack spacing={2} sx={{ pt: `${HF_SPACING.md}px` }}>
                <TextField
                    fullWidth
                    size='small'
                    placeholder={t('SEARCH')}
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    autoFocus
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <Search fontSize='small' />
                            </InputAdornment>
                        )
                    }}
                />

                <Box display='flex' justifyContent='center'>
                    <ButtonGroup variant='outlined' aria-label={t('CHANGE_COMPANY')}>
                        {(['all', 'active', 'inactive'] as const).map((filter) => (
                            <Button
                                key={filter}
                                variant={statusFilter === filter ? 'contained' : 'outlined'}
                                onClick={() => handleStatusFilterChange(filter)}
                                disabled={showGridSkeleton || changingCompany}
                            >
                                {getStatusFilterLabel(filter)}
                            </Button>
                        ))}
                    </ButtonGroup>
                </Box>

                {renderGridContent()}

                {!showGridSkeleton && total > COMPANIES_PER_PAGE && (
                    <Box display='flex' justifyContent='center'>
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={handlePageChange}
                            color='primary'
                            size='small'
                            disabled={changingCompany}
                        />
                    </Box>
                )}

                {showGridSkeleton && total > COMPANIES_PER_PAGE && (
                    <Box display='flex' justifyContent='center'>
                        <Skeleton variant='rounded' width={280} height={32} />
                    </Box>
                )}
            </Stack>
        </HfDialog>
    );
};

export default HfCompanySelectorModal;
